import { t } from "@lingui/macro";
import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { AnalyzeResumeResult, MetricKeyEnum } from "@reactive-resume/schema";
import { Group } from "@visx/group";
import { SeriesPoint } from "@visx/shape/lib/types";
import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale";
import { AxisBottom, AxisLeft, AxisRight } from "@visx/axis";
import { withTooltip } from "@visx/tooltip";
import { WithTooltipProvidedProps } from "@visx/tooltip/lib/enhancers/withTooltip";
import { useTheme } from "@reactive-resume/hooks";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { MetricScoreTooltip } from "./metric-score-tooltip";

type MetricsBarChartProps = {
  analysesData: AnalyzeResumeResult;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  events?: boolean;
  onMetricClick?: (metric: { id: MetricKeyEnum; title: string }) => void;
};

type MetricData = {
  metric: string;
  value: number;
  fullName: string;
  description: string;
  color: string;
  abbreviatedName: string;
  weight: number;
  normalizedWeight: number;
  id: MetricKeyEnum;
};

type TooltipData = {
  data: MetricData;
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
};

// Define color scheme based on score - unified with mandatory-requirements-checklist.tsx
const getScoreColor = (score: number, isDarkMode: boolean): string => {
  // Unified colors with mandatory-requirements-checklist.tsx
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    red: isDarkMode ? "#ef5350" : "#e57373",
    blue: "#4dabf5",
  };

  // Use same thresholds as metric-score-card.tsx
  if (score >= 0.8) return colors.green; // excellent
  if (score >= 0.6) return colors.blue; // good
  return colors.red; // needs_work
};

// Get contrasting text color based on background color and theme
const getContrastingTextColor = (
  backgroundColor: string,
  isDarkMode: boolean,
  barWidth: number,
  textWidth: number = 0,
): string => {
  // Always use theme-appropriate color since labels are now above bars
  return isDarkMode ? "#f1f5f9" : "#1e293b"; // slate-100 : slate-800
};

// Score ranges for legend - unified with mandatory-requirements-checklist.tsx
const scoreRanges = [
  { range: "0-59%", color: "#ef5350" }, // red - needs work
  { range: "60-79%", color: "#4dabf5" }, // blue - good
  { range: "80-100%", color: "#66bb6a" }, // green - excellent
];

// Adjust margins for vertical layout
const getMargins = (containerWidth: number) => {
  // For narrow containers, use smaller margins
  if (containerWidth < 600) {
    return { top: 40, left: 25, right: 20, bottom: 80 };
  }
  // For medium containers
  if (containerWidth < 900) {
    return { top: 40, left: 35, right: 30, bottom: 80 };
  }
  // For wide containers, use default margins
  return { top: 40, left: 45, right: 40, bottom: 80 };
};

// Format the percentage to show whole numbers with % symbol
const formatPercentage = (value: number): string => {
  return `${Math.round(value * 100)}%`;
};

// Abbreviate long metric names - make more aggressive to save space
const abbreviateMetricName = (name: string): string => {
  // If the name is already short, return it as is
  if (name.length <= 25) return name;

  // Otherwise abbreviate it
  const words = name.split(" ");
  if (words.length <= 2) return name;

  // For very long names, take first two words and add ellipsis
  return words.slice(0, 2).join(" ") + "...";
};

function MetricsBarChartBase({
  analysesData,
  height = 600,
  margin: propMargin,
  events = false,
  tooltipOpen,
  tooltipLeft,
  tooltipTop,
  tooltipData,
  hideTooltip,
  showTooltip,
  onMetricClick,
}: MetricsBarChartProps & WithTooltipProvidedProps<TooltipData>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(300);
  const [isAnimated, setIsAnimated] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const hasAnimatedRef = useRef(false);
  const tooltipTimeoutRef = useRef<number>();
  const { isDarkMode } = useTheme();

  // Calculate margins based on container width
  const margin = useMemo(() => {
    return propMargin || getMargins(width);
  }, [width, propMargin]);

  // Colors for dark/light themes
  const textColor = isDarkMode ? "#e2e8f0" : "#334155"; // slate-200 : slate-700
  const axisColor = isDarkMode ? "#64748b" : "#94a3b8"; // slate-500 : slate-400
  const gridColor = isDarkMode ? "#475569" : "#94a3b8"; // slate-600 : slate-400

  // Start animation only once when component mounts
  useEffect(() => {
    if (!hasAnimatedRef.current) {
      const timer = setTimeout(() => {
        setIsAnimated(true);
        hasAnimatedRef.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Memoized hover handlers
  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<SVGElement>, barData: TooltipData, index: number) => {
      // Clear any existing timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }

      setHoveredIndex(index);
      const coords = localPoint(event) || { x: 0, y: 0 };
      showTooltip({
        tooltipData: barData,
        tooltipTop: coords.y,
        tooltipLeft: coords.x + 10,
      });
    },
    [showTooltip],
  );

  const handleMouseLeave = useCallback(
    (index: number) => {
      setHoveredIndex(null);
      // Add a small delay before hiding tooltip to prevent flickering
      tooltipTimeoutRef.current = window.setTimeout(() => {
        hideTooltip();
      }, 150);
    },
    [hideTooltip],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGElement>, barData: TooltipData) => {
      // Clear any existing timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }

      const coords = localPoint(event) || { x: 0, y: 0 };
      showTooltip({
        tooltipData: barData,
        tooltipTop: coords.y,
        tooltipLeft: coords.x + 10,
      });
    },
    [showTooltip],
  );

  // Update width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth);
      }
    };

    // Initial update
    updateWidth();

    // Add resize observer for more reliable size updates
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Fallback to window resize event
    window.addEventListener("resize", updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // Process metrics data
  const metrics = useMemo(() => {
    if (!analysesData?.metrics) return [];

    // First, collect all metrics with their weights
    const metricsWithWeights = Object.entries(analysesData.metrics).map(([key, metric]) => {
      // Calculate the actual score by dividing the weightedScore by the weight
      const actualScore = metric.weight > 0 ? metric.weightedScore / metric.weight : 0;

      return {
        metric: metric.metric,
        shortKey: key.toUpperCase(),
        description: metric.description,
        value: actualScore,
        color: getScoreColor(actualScore, isDarkMode),
        abbreviatedName: abbreviateMetricName(metric.metric),
        weight: metric.weight,
        id: key as MetricKeyEnum,
      };
    });

    // Calculate total weight to normalize
    const totalWeight = metricsWithWeights.reduce((sum, m) => sum + m.weight, 0);

    // Normalize weights and add normalized weight
    const normalizedMetrics = metricsWithWeights.map((m) => ({
      ...m,
      normalizedWeight: totalWeight > 0 ? m.weight / totalWeight : 1 / metricsWithWeights.length,
    }));

    // Sort by weight (importance) instead of score
    return normalizedMetrics.sort((a, b) => b.weight - a.weight);
  }, [analysesData, isDarkMode]);

  if (!metrics.length || width < 10) return null;

  // Format data for chart
  const data: MetricData[] = [...metrics].map((m) => ({
    metric: m.metric,
    value: m.value,
    fullName: m.metric,
    description: m.description,
    color: m.color,
    abbreviatedName: m.abbreviatedName,
    weight: m.weight,
    normalizedWeight: m.normalizedWeight,
    id: m.id,
  }));

  // Adjust height based on number of metrics for better visualization
  // Each metric bar takes the full allocated space with more room for labels
  const itemHeight = 60;
  const adjustedHeight = Math.max(350, data.length * itemHeight + margin.top + margin.bottom);

  // Bounds
  const xMax = Math.max(10, width - margin.left - margin.right);
  const yMax = adjustedHeight - margin.top - margin.bottom;

  // Scales
  const valueScale = scaleLinear<number>({
    domain: [0, 1],
    nice: true,
    range: [0, xMax],
  });

  // New scale for vertical positioning of each metric group with more padding
  const metricScale = scaleBand<string>({
    domain: data.map((d) => d.metric),
    padding: 0.5,
    range: [0, yMax],
  });

  return (
    <div className="w-full overflow-hidden" ref={containerRef}>
      <div className="relative overflow-hidden">
        <svg
          width="100%"
          height={adjustedHeight}
          viewBox={`0 0 ${width - 2} ${adjustedHeight - 2}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Metrics visualization"
        >
          {/* Define gradients */}
          <defs>
            {/* Shadow filter */}
            <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.15)" />
            </filter>
          </defs>

          {/* Add subtle grid lines */}
          <Group top={margin.top} left={margin.left}>
            {valueScale.ticks(10).map((tick) => (
              <line
                key={`grid-line-${tick}`}
                x1={valueScale(tick)}
                x2={valueScale(tick)}
                y1={0}
                y2={yMax}
                stroke={gridColor}
                strokeWidth={0.8}
                strokeDasharray="4,4"
                opacity={0.5}
              />
            ))}
          </Group>

          <Group top={margin.top} left={margin.left}>
            {/* Render metrics vertically */}
            {data.map((d, i) => {
              const metricY = metricScale(d.metric);
              if (metricY === undefined) return null;

              const barWidth = valueScale(d.value);
              const fullBandwidth = metricScale.bandwidth();
              const barHeight = fullBandwidth * 0.7; // Use 70% of bandwidth to make bars narrower
              const barY = metricY + (fullBandwidth - barHeight) / 2; // Center the bar vertically
              const isHovered = hoveredIndex === i;

              // Bar data for tooltip
              const barData = {
                data: d,
                x: 0,
                y: barY,
                width: barWidth,
                height: barHeight,
                index: i,
              };

              // Find the metric ID from the processed metrics
              const metricInfo = metrics.find((m) => m.metric === d.metric);

              return (
                <g
                  key={`metric-group-${i}`}
                  className={onMetricClick ? "cursor-pointer" : ""}
                  onClick={() => {
                    if (onMetricClick && metricInfo) {
                      onMetricClick({
                        id: metricInfo.id,
                        title: d.metric,
                      });
                    }
                  }}
                  style={{
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {/* Interactive overlay for consistent hover area */}
                  <rect
                    x={-8}
                    y={barY - 25} // Extended upward to include label area
                    width={xMax + 16}
                    height={barHeight + 30} // Extended height for label
                    fill="transparent"
                    rx={8}
                    ry={8}
                    style={{
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      fill: isHovered
                        ? isDarkMode
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.02)"
                        : "transparent",
                      stroke:
                        onMetricClick && isHovered
                          ? isDarkMode
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.05)"
                          : "transparent",
                      strokeWidth: 1,
                      strokeDasharray: onMetricClick ? "2,2" : "none",
                      pointerEvents: "all", // Ensure this element receives mouse events
                    }}
                    onMouseEnter={(event) => handleMouseEnter(event, barData, i)}
                    onMouseLeave={() => handleMouseLeave(i)}
                    onMouseMove={(event) => handleMouseMove(event, barData)}
                  />

                  {/* Clickable indicator - small icon that appears on hover */}
                  {onMetricClick && isHovered && (
                    <g style={{ pointerEvents: "none" }}>
                      <rect
                        x={xMax - 20 - 10}
                        y={barY - 15}
                        width={32}
                        height={32}
                        rx={8} // round corners (adjust as needed)
                        ry={8}
                        fill={isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}
                        stroke={isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"}
                        strokeWidth={1}
                      />
                      <g transform={`translate(${xMax - 24}, ${barY - 10}) scale(0.08)`}>
                        <path
                          d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"
                          fill={isDarkMode ? "#94a3b8" : "#64748b"}
                        />
                      </g>
                    </g>
                  )}

                  {/* Background bar with subtle glass effect */}
                  <rect
                    x={0}
                    y={barY}
                    width={xMax}
                    height={barHeight}
                    fill={isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"}
                    rx={6}
                    ry={6}
                    style={{
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      fill: isHovered
                        ? isDarkMode
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.04)"
                        : isDarkMode
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.02)",
                      pointerEvents: "none", // Prevent this from blocking mouse events
                    }}
                  />

                  {/* Main progress bar */}
                  <rect
                    x={isHovered ? -2 : 0}
                    y={isHovered ? barY - 2 : barY}
                    width={isHovered ? barWidth + 4 : barWidth}
                    height={isHovered ? barHeight + 4 : barHeight}
                    fill={d.color}
                    rx={5}
                    ry={5}
                    style={{
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      pointerEvents: "none", // Prevent this from blocking mouse events
                    }}
                  />

                  {/* Metric title and percentage - positioned above the bar */}
                  <text
                    x={8}
                    y={barY - 8} // Positioned above the bar
                    fontSize={Math.max(13, Math.min(16, barHeight * 0.35))}
                    fontWeight={600}
                    fill={getContrastingTextColor(d.color, isDarkMode, barWidth, 0)}
                    style={{
                      opacity: isHovered ? 1 : 0.9,
                      pointerEvents: "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      filter: isDarkMode
                        ? "drop-shadow(0 1px 2px rgba(0,0,0,0.5))"
                        : "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                    }}
                  >
                    {d.metric}
                  </text>

                  {/* Percentage number - larger and more prominent */}
                  <text
                    x={8 + d.metric.length * 8} // Position after the metric name
                    y={barY - 8}
                    fontSize={Math.max(16, Math.min(20, barHeight * 0.45))} // Increased font size
                    fontWeight={800} // Extra bold
                    fill={d.color}
                    style={{
                      opacity: isHovered ? 1 : 0.95,
                      pointerEvents: "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      filter: isDarkMode
                        ? "drop-shadow(0 2px 4px rgba(0,0,0,0.7)) drop-shadow(0 0 8px rgba(0,0,0,0.3))"
                        : "drop-shadow(0 2px 4px rgba(0,0,0,0.2)) drop-shadow(0 0 4px rgba(255,255,255,0.8))",
                    }}
                  >
                    {formatPercentage(d.value)}
                  </text>
                </g>
              );
            })}

            {/* Y-axis with sorting label */}
            <text
              x={-20}
              y={yMax / 2}
              fill={isDarkMode ? "#94a3b8" : "#64748b"}
              fontSize={12}
              fontWeight={600}
              textAnchor="middle"
              transform={`rotate(-90, -20, ${yMax / 2})`}
            >
              ↓ {t`Sorted by importance`}
            </text>

            <AxisBottom
              top={yMax}
              scale={valueScale}
              stroke={axisColor}
              tickStroke={axisColor}
              tickValues={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
              tickFormat={(value) => formatPercentage(Number(value))}
              label={t`Match Score`}
              tickLabelProps={() => ({
                fill: textColor,
                fontSize: 9,
                textAnchor: "middle",
                dy: 8,
              })}
              labelProps={{
                fill: isDarkMode ? "#94a3b8" : "#64748b",
                fontSize: 12,
                fontWeight: 600,
                textAnchor: "middle",
                dy: 20,
              }}
            />
          </Group>
        </svg>

        <MetricScoreTooltip
          open={tooltipOpen && !!tooltipData}
          fullName={tooltipData?.data.fullName || ""}
          score={tooltipData?.data.value || 0}
          importance={tooltipData?.data.weight || 0}
          description={tooltipData?.data.description || ""}
          top={tooltipTop ?? 0}
          left={tooltipLeft ?? 0}
        />
      </div>
    </div>
  );
}

// Add proper import for localPoint
const localPoint = (event: React.MouseEvent<SVGElement>) => {
  if (!event) return { x: 0, y: 0 };

  // Get coordinates relative to SVG
  const target = event.target as SVGElement;
  const svg = target.ownerSVGElement;
  if (!svg) return { x: 0, y: 0 };

  const point = svg.createSVGPoint();
  point.x = event.clientX;
  point.y = event.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };

  return point.matrixTransform(ctm.inverse());
};

export const MetricsBarChart = withTooltip<MetricsBarChartProps, TooltipData>(MetricsBarChartBase);
